import React from 'react';
import { IconsKey, Glyphicon } from '../icons';
import styled, { css } from '../../styles/styled-components';

interface ListProps {
  items?: Array<ListItemProps & { key: string | number }>;
  divided?: boolean;
  horizontal?: boolean;
  itemsMargin?: number;
}

export const List: React.SFC<ListProps> = ({
  items,
  children,
  divided,
  horizontal,
  ...rest
}) => (
  <Ul divided={divided} horizontal={horizontal} {...rest}>
    {items
      ? items.map(({ key, ...item }) => (
          <ListItem key={key.toString()} {...item} />
        ))
      : children}
  </Ul>
);

type UlProps = {
  divided?: boolean;
  horizontal?: boolean;
  itemsMargin?: number;
} & JSX.IntrinsicElements['ul'];

const cssItemsMargin = (itemsMargin?: number, horizontal?: boolean) => {
  if (!itemsMargin) return '';

  if (!horizontal) {
    return css`
      ${ListItemContent} {
        margin-bottom: ${itemsMargin}px;
      }

      ${ListItemContent}:last-child {
        margin-bottom: 0;
      }
    `;
  } else {
    return css`
      ${ListItemContent} {
        margin-right: ${itemsMargin}px;
      }

      ${ListItemContent}:last-child {
        margin-right: 0;
      }
    `;
  }
};

const cssDivided = (divided?: boolean, horizontal?: boolean) => {
  if (!divided) return '';

  if (!horizontal) {
    return css`
      ${ListItemContent} {
        border-bottom: 1px solid ${p => p.theme.dividerColor};
      }

      ${ListItemContent}:last-child {
        border-bottom: 0;
      }
    `;
  } else {
    return css`
      ${ListItemContent} {
        border-right: 1px solid ${p => p.theme.dividerColor};
      }

      ${ListItemContent}:last-child {
        border-right: 0;
      }
    `;
  }
};

const Ul = styled<UlProps>(({ divided, horizontal, itemsMargin, ...rest }) => (
  <ul {...rest} />
))`
  list-style: none;
  margin: 0;
  padding: 0;

  ${p =>
    p.horizontal
      ? css`
          display: flex;
        `
      : ''};

  ${p => cssItemsMargin(p.itemsMargin, p.horizontal)};
  ${p => cssDivided(p.divided, p.horizontal)};
`;

interface ListItemProps {
  content?: React.ReactChild;
  header?: React.ReactChild;
  description?: React.ReactChild;
  icon?: IconsKey;
  showOnHover?: React.ReactChild;
}

export const ListItem: React.SFC<ListItemProps> = ({
  content,
  icon,
  header,
  description,
  showOnHover,
  children,
  ...rest
}) => {
  if (children) {
    return <ListItemContent {...rest}>{children}</ListItemContent>;
  } else if (content) {
    return (
      <ListItemContent flex {...rest}>
        {icon && <ListItemIcon key="icon" glyph={icon} />}
        {content}
        {showOnHover && (
          <ListItemShowOnHover key="showOnHover">
            {showOnHover}
          </ListItemShowOnHover>
        )}
      </ListItemContent>
    );
  } else if (header && description) {
    return (
      <ListItemContent flex="column" {...rest}>
        <ListItemHeader>{header}</ListItemHeader>
        <ListItemDescription>{description}</ListItemDescription>
        {!showOnHover ? (
          ''
        ) : (
          <ListItemShowOnHover key="showOnHover">
            {showOnHover}
          </ListItemShowOnHover>
        )}
      </ListItemContent>
    );
  }
};

// const Li = styled.li`
//   display: flex;
//   align-items: center;
//   padding: 5px;
//   text-align: left;
// `;

type ListItemContentProps = {
  flex?: boolean | 'column' | 'row';
} & JSX.IntrinsicElements['li'];

export const ListItemContent = styled<ListItemContentProps>(
  ({ flex, ...rest }) => <li {...rest} />,
)`
  flex-grow: 1;
  padding: 10px 5px;

  ${p => {
    switch (p.flex) {
      case true:
      case 'row':
        return css`
          display: flex;
          flex-direction: row;
        `;
      case 'column':
        return css`
          display: flex;
          flex-direction: column;
        `;
      case false:
        return css`
          display: block;
        `;
    }
  }};
`;

export const ListItemShowOnHover = styled.div`
  opacity: 0;
  margin-top: 5px;

  ${ListItemContent}:hover & {
    opacity: 1;
  }
`;

const IconContainer = styled.div`
  padding: 0 10px;
`;

export const ListItemIcon = styled(Glyphicon)`
  padding: 0 10px 0 0;
`;

export const ListItemHeader = styled.div`
  // font-weight: bold;
  margin-bottom: 5px;
`;

export const ListItemDescription = styled.div`
  color: gray;
  font-size: 0.8em;
`;
